// src/app/argent/useSynchroDonnees.js — relecture des données, import iCal,
// journal des factures (saveLog), états d'envoi et verrou anti-double-émission
// (factureOpLock).
// CHEMIN ARGENT. Déplacé octet pour octet depuis src/App.jsx (P-3.5, squelette,
// partie 2) : seuls changent l'emballage (signature, retour) et les imports.

import { storage } from '../../supabase'
import { useState, useRef, useEffect } from 'react'
import { migrateData } from '../../lib/data-migration'
// Lot AUTO-REFRESH — verrou anti-concurrence refreshAllIcal (audit perf §2/QW2)
// + purge du cache stale-while-revalidate à la déconnexion (hygiène session).
import { createSingleFlightState, runSingleFlight } from '../../lib/single-flight'
import { jetonDetenu, fetchData, saisieEnCours, appliquerRelecture, preparerEcrituresImport } from '../../lib/data-api'
import { createSaveLog } from '../../lib/factlog-save'
import { importIcalForLog } from '../../lib/channels/ical'

export function useSynchroDonnees({
  cfg, setData, setGardeAlert, setFactLog, factLogRef, setStockCentral, setDepots, setAchats, month,
  confirm, resaModal, prorataModal, extraModal, ajustModal, currentUser, saveData,
  montrerBandeauData, retirerBandeauData,
}) {
  // ── Auto-refresh iCal au chargement + toutes les 30 min ──
  const [icalRefreshing, setIcalRefreshing] = useState(false)
  const [sendingEmail, setSendingEmail] = useState(null) // {prop, log} en cours d'envoi
  const [genBusy, setGenBusy] = useState(false) // "Générer la facture" en cours (UI: désactive boutons)
  // BUG 3 (aperçu commission) : séquences de factures pré-chargées (lecture seule)
  // pour afficher le PROCHAIN numéro PRÉVU dans l'aperçu commission. Pré-chargées
  // au mount (et NON à l'ouverture de la modale) pour que handlePreviewFacture
  // reste 100% synchrone → window.open jamais bloqué par le popup-blocker. Si non
  // chargées / échec, l'aperçu retombe sur le fallback `{prefix}-XXXX`.
  const [invoiceSequences, setInvoiceSequences] = useState(null)
  const [emailSent, setEmailSent] = useState({}) // {factureNum: true}
  const [iaAnalyse, setIaAnalyse] = useState(null) // résultat analyse business
  const [iaAnalyseLoading, setIaAnalyseLoading] = useState(false)
  // v3 ÉTAPE 1 (read-path Beds24) — l'indicateur du bandeau n'affiche plus
  // l'heure du dernier clic iCal V1 legacy (trompeur). On conserve le setter
  // (marqueur de fin de sync iCal → re-render) mais la valeur n'est plus lue.
  const [, setIcalLastSync] = useState(null)
  // N5 — l'ancien deep-link « clic-jour Mois → menage-today(date) » est supprimé :
  // le détail du jour se déplie désormais INLINE dans le Mois (MenageDayDetail).
  // 'menage-today' n'est donc plus seedé par une date externe (il défaut sur
  // aujourd'hui, avec sa propre barre nav J-1/J+1 interne).

  // Refs pour éviter les stale closures dans les intervals et listeners
  const monthRef = useRef(month)
  const cfgRef = useRef(cfg)
  const saveDataRef = useRef(saveData)
  // Verrou SYNCHRONE anti-double-émission facture commission proprio. Un ref (pas
  // un state) car il doit bloquer un 2e clic dans le MÊME tick React, avant tout
  // re-render/refresh (le disabled UI + la garde invoicesRaw ne se mettent à jour
  // qu'au render suivant → fenêtre de doublon). Posé/relâché dans sendFactureEmail
  // ET doGenFacture (chemin argent : invariant « 1 commission / mois / logement »).
  const factureOpLock = useRef(false)
  useEffect(() => { monthRef.current = month }, [month])
  useEffect(() => { cfgRef.current = cfg }, [cfg])
  useEffect(() => { saveDataRef.current = saveData }, [saveData])
  // F13 — « une saisie est-elle en cours ? », lu par le rafraîchissement
  // (qui tourne dans un intervalle, d'où le ref). Les fenêtres d'édition de
  // l'écran Argent ; le curseur dans un champ est lu au moment même.
  const modaleArgentOuverteRef = useRef(false)
  useEffect(() => {
    modaleArgentOuverteRef.current = !!(resaModal || prorataModal || extraModal || ajustModal || confirm)
  }, [resaModal, prorataModal, extraModal, ajustModal, confirm])

  // Lot AUTO-REFRESH (audit perf §2/QW2) — VERROU anti-concurrence.
  // Constat mesuré : les DEUX useEffect au mount ([ready,currentUser] et
  // [month,currentUser]) déclenchaient refreshAllIcal en parallèle → 10 flux
  // iCal fetchés chacun 2 fois (20 appels) + fbz8-data lu 2 fois (2×299 KB).
  // Fix : single-flight partagé entre tous les déclencheurs (mount, interval
  // 5 min, visibilitychange, changement de mois). Un trigger qui arrive
  // PENDANT un run en vol n'est pas perdu : il est rejoué UNE fois à la fin
  // (trailing rerun) — un changement de mois pendant la sync garde donc sa
  // fraîcheur, sans jamais deux runs concurrents. La ZONE D'ÉCRITURE
  // (importIcalForLog + garde 50 % saveData) est STRICTEMENT intouchée —
  // seul le déclenchement est verrouillé.
  const icalFlightRef = useRef(createSingleFlightState())
  const refreshAllIcal = () => runSingleFlight(icalFlightRef.current, doRefreshAllIcal)

  // F13 (tour 3) : LA relecture — lire /api/data et appliquer la décision
  // (adopter, bandeau, rien), SANS écrire. Partagée par le rafraîchissement
  // automatique (qui importe ensuite) et par la fermeture de la fenêtre CSV
  // après un refus (qui ne fait que relire). Le corps est le bloc de lecture
  // de doRefreshAllIcal, déplacé à l'identique.
  async function relireDonnees() {
    // Toujours récupérer les données fraîches depuis Supabase.
    // Garde critique : si la lecture échoue, on abort sans toucher à fbz8-data.
    // Sinon la boucle saveData écraserait le blob avec un état vide + résas iCal
    // du mois courant uniquement (cf. incident 20-21 avril 2026 : 117 résas → 37).
    let freshData = null
    let jetonLu = ''
    // Noté AVANT la relecture : un enregistrement qui aboutit pendant qu'on
    // relit fait abandonner la relecture (cf. appliquerRelecture).
    const jetonAvant = jetonDetenu()
    try {
      // F13 — cette lecture NE touche PLUS au jeton détenu : elle le rend, et
      // c'est la décision plus bas qui adopte données ET jeton ensemble, ou
      // aucun des deux. Avant, elle remplaçait le jeton sans mettre l'écran à
      // jour quand l'iCal n'ajoutait rien : l'onglet périmé recevait un jeton
      // frais et pouvait écraser en silence un geste fait ailleurs.
      const lu = await fetchData()
      const dr = lu.data
      jetonLu = lu.jeton
      // PR #146 BUG 2 root cause (2026-05-09 — cf. rapport pessimist deep
      // docs/handoff/investigation-bug2-garde-cote-admin-2026-05-09.md) :
      // si storage.get retourne null (RLS silencieux, JWT mid-rotation
      // Supabase ~50min, erreur réseau preview), ABORT au lieu de fallback {}.
      // Sinon importIcalForLog voit existing=[] pour chaque log → écrit un
      // blob partiel (mois courant + résas iCal seulement) → la garde 50%
      // saveData déclenche correctement (donc PAS de corruption data) mais
      // le banner alarme l'admin légitime à tort.
      // ⚠️ /api/data rend `{}` sur base vide, là où `storage.get` rendait
      // `null` : sans ce test de vacuité, la garde d'abandon ci-dessous ne se
      // déclencherait plus, et un blob vide partirait dans importIcalForLog —
      // exactement l'incident des 20-21 avril 2026 (117 résas → 37).
      freshData = dr && Object.keys(dr).length > 0 ? migrateData(dr) : null
    } catch(e){ console.error('[refreshAllIcal/loadData] abort refresh :', e) }
    if (freshData === null) return { continuer: false, freshData: null, jetonLu }
    // F13 — décision de Dimitri (2026-10-02) : des données plus récentes se
    // rechargent SEULES si rien n'est en cours de saisie ; sinon bandeau, on
    // garde l'ancien jeton (l'enregistrement sera refusé en 409) et on
    // n'importe rien — l'import enregistrerait par-dessus la saisie.
    // Même jeton et saisie en cours : on n'importe rien non plus (point 5) —
    // l'import reprendra au passage suivant.
    // Lue au moment même (fenêtres d'édition, curseur dans un champ Argent),
    // ici puis avant CHAQUE écriture de l'import (F13 suite, point 5).
    const saisieArgentEnCours = () => saisieEnCours({
      modaleOuverte: modaleArgentOuverteRef.current,
      elementActif: typeof document !== 'undefined' ? document.activeElement : null,
      racine: typeof document !== 'undefined' ? document : null,
    })
    const continuer = appliquerRelecture({
      jetonAvant,
      jetonLu,
      donnees: freshData,
      enSaisie: saisieArgentEnCours(),
      adopterDonnees: (d) => setData(d),
      montrerBandeau: montrerBandeauData,
      retirerBandeau: retirerBandeauData,
    })
    return { continuer, freshData, jetonLu, saisieArgentEnCours }
  }

  async function doRefreshAllIcal() {
    // ANTI-RÉCIDIVE incident 2026-05-09 (ADR-077) : defense-in-depth.
    // Les useEffect appelants skip déjà role=menage, mais on re-vérifie ici
    // au cas où un trigger non-identifié appellerait refreshAllIcal depuis
    // ailleurs. Le ménage n'a aucun usage de la sync iCal et la RLS storage
    // (tightened côté DB) bloque la lecture partielle qui provoquait l'écrasement.
    // Permissions 42 Phase 2 : le rôle proprietaire (espace dédié lecture seule)
    // n'a aucun usage de la sync iCal admin et n'a pas le cfg chargé.
    // Permissions 42 Phase 3 Lot 0 : l'orchestrateur est un AGENT (non-admin) →
    // aucun usage de la sync iCal, comme le ménage. Defense-in-depth (les seuls
    // call sites useEffect skippent déjà sur role!=='admin').
    if (currentUser?.role === "menage" || currentUser?.role === "proprietaire" || currentUser?.role === "orchestrateur") {
      console.warn(`[refreshAllIcal] skip — role=${currentUser?.role} (defense-in-depth ADR-077)`)
      return 0
    }
    // Lire les valeurs courantes via les refs pour éviter les stale closures
    const currentCfg = cfgRef.current
    const currentSaveData = saveDataRef.current
    const currentMonth = monthRef.current
    const [currentY, currentM] = currentMonth.split("-").map(Number)
    setIcalRefreshing(true)
    const allLogs = currentCfg.props.flatMap(p => p.logs)
    // Inclure les logs normaux + les chambres de sous-location
    const logsWithIcal = [
      ...allLogs.filter(l => l.ical),
      ...allLogs.flatMap(l => (l.chambres||[]).filter(c => c.ical))
    ]
    let totalAdded = 0
    const { continuer, freshData: freshLu, jetonLu, saisieArgentEnCours } = await relireDonnees()
    let freshData = freshLu
    if (!continuer) {
      setIcalRefreshing(false)
      return 0
    }
    // F13 — les écritures de l'import portent le jeton des données dont elles
    // partent ; si Dimitri enregistre pendant l'import, l'import s'arrête
    // (bandeau) au lieu d'écraser sa saisie avec l'état relu avant elle.
    // Une saisie qui commence pendant l'import l'arrête avant sa prochaine
    // écriture ; il reprendra au passage suivant.
    const ecritures = preparerEcrituresImport({ jetonLu, montrerBandeau: montrerBandeauData, enSaisie: saisieArgentEnCours })
    for (const log of logsWithIcal) {
      if (ecritures.arrete()) break
      try {
        const added = await importIcalForLog(log, currentMonth, currentY, currentM, freshData, async (nd) => {
          const jeton = ecritures.jetonPourEcrire()
          if (jeton === null) return false
          const ok = await currentSaveData(nd, { jeton, surConflit: ecritures.conflit })
          ecritures.apresEcriture(ok)
          if (ok) freshData = nd
          return ok
        })
        // Un ajout dont l'écriture a été arrêtée n'est pas un ajout.
        if (!ecritures.arrete()) totalAdded += added
      } catch(e){ console.error('[refreshAllIcal/importIcalForLog]', log?.id, e) }
    }
    setIcalLastSync(new Date())
    setIcalRefreshing(false)
    return totalAdded
  }
  // saveLog accepte soit un ARRAY (rétro-compat) soit un UPDATER `prev => next`.
  // La valeur est calculée HORS de setFactLog (la capturer dans l'updater a
  // figé le journal du 02/06 au 01/10/2026) ; un échec s'affiche dans le
  // bandeau. Détail : createSaveLog (src/lib/factlog-save.js).
  const saveLog = createSaveLog({
    getCurrent: () => factLogRef.current,
    setCurrent: (next) => { factLogRef.current = next; setFactLog(next) },
    write: (texte) => storage.set("fbz8-factlog", texte),
    onEchec: (message) => setGardeAlert({ type: 'ecriture', message, userRole: currentUser?.role || 'unknown' }),
  })
  // LOT Incidents 13 — saveIncidents (writer KV fbz8-incidents) RETIRÉ :
  // la table V2 `incidents` est la source unique admin, plus aucune écriture KV
  // côté admin. Le state `incidents` est désormais en lecture seule (chargé V2).
  // Lot 1 : applique le snapshot stock renvoyé par /api/stock (cascade + dual-write
  // déjà faits serveur). Remplace les anciens save* KV directs. La persistance
  // (tables + miroir KV) se fait côté serveur ; ici on ne fait que re-synchroniser
  // l'état React. ComptaTab consomme `depots` depuis ce state (lecture seule).
  const applyStockSnapshot = (snap) => {
    if (!snap) return
    if (snap.stockCentral) setStockCentral(snap.stockCentral)
    if (snap.depots) setDepots(snap.depots)
    if (snap.achats) setAchats(snap.achats)
  }
  return {
    icalRefreshing, setSendingEmail, sendingEmail, setGenBusy, genBusy, setInvoiceSequences,
    invoiceSequences, setEmailSent, iaAnalyse, setIaAnalyse, setIaAnalyseLoading, iaAnalyseLoading,
    factureOpLock, refreshAllIcal, relireDonnees, saveLog, applyStockSnapshot,
  }
}

export function useSynchroAuto({ month, ready, currentUser, refreshAllIcal }) {
  // Auto-refresh iCal au premier chargement + toutes les 5 min (sans stale closure)
  // Phase 4 PR 5 fix : le role=menage n'a aucun usage de la sync iCal et la
  // RLS table storage filtre fbz8-data → le freshData arrive partiel → garde
  // saveData 50% déclenche systématiquement. Skip pour menage.
  useEffect(() => {
    if (!ready) return
    if (currentUser?.role !== "admin") return // menage + proprietaire : pas de sync iCal
    refreshAllIcal()
    const interval = setInterval(() => refreshAllIcal(), 5 * 60 * 1000)
    // Relancer le sync quand l'app redevient visible (mobile arrière-plan)
    const onVisible = () => { if (document.visibilityState === 'visible') refreshAllIcal() }
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [ready, currentUser])

  // Sync iCal quand on change de mois (admin uniquement)
  // Lot AUTO-REFRESH (QW2) — DÉDUP du déclenchement : au 1er passage éligible
  // (mount / login admin), l'effet [ready,currentUser] ci-dessus lance DÉJÀ la
  // sync — cet effet-ci ne doit tirer QUE sur un VRAI changement de mois.
  // Sans ce skip, les deux effets partaient en même temps au mount (le verrou
  // single-flight dédouble aussi, mais autant ne pas déclencher du tout).
  const icalMonthEffectPrimed = useRef(false)
  useEffect(() => {
    if (!ready) return
    if (currentUser?.role !== "admin") return // menage + proprietaire exclus
    if (!icalMonthEffectPrimed.current) { icalMonthEffectPrimed.current = true; return }
    refreshAllIcal()
  }, [month, currentUser])
  return {  }
}
